// TopNavLayout.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import Icon from "./common/MaterialIcon";
import TaskIcon from "./common/TaskIcon";
import { Badge, Dropdown } from "flowbite-react";
import { IoIosArrowDown, IoMdRefresh } from "react-icons/io";
import { MdOutlinePayments } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery } from "react-query";
import { useApplication } from "../contexts/ApplicationContext";
import { useTheme } from "../contexts/ThemeContext";
import { useSocketReady, useSocketRef } from "../contexts/SocketContext";
import {
  hasPermission,
  hasAnyPermission,
  removeToken,
} from "../utils/permissions";
import { calculateExpirationPeriodTest, formatToReal } from "../utils/utils";
import axios from "../config/axiosInstance";
import { CALENDAR, CRM, FILIAL, WHATSAPP } from "../config/apiConfig";
import NotificationBell from "./NotificationBell";
import AnimatedToast from "./common/AnimatedToast";
import GlobalSearchPalette from "./common/GlobalSearchPalette";
import ThemeToggle from "./common/ThemeToggle";
import GlobalChatDock from "../pages/GlobalChatDock";
import { Tooltip } from "react-tooltip";
import "./Sidebar.css";
import { normalizeAvatarUrl } from "../utils/avatarUtils";
import { decodeJwtPayload } from "../utils/jwt";
import { HiChatAlt, HiSearch } from "react-icons/hi";
import {
  describeSwitchCompanyError,
  sortCompaniesAlphabetically,
  switchActiveCompany,
} from "../utils/companies";
import { alertDialog } from "../utils/dialogs";

const SIDEBAR_COLLAPSED_KEY = "relinq-sales-sidebar-collapsed";
// Dia de calendário em Brasília, que é o fuso do banco e o que a tela "Hoje"
// considera. Com `toISOString()` cru, das 21h à meia-noite o dia já virou em UTC
// e o contador do menu passava a somar as tarefas de amanhã.
const toISODate = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
};
const isInboundDirection = (direction) => {
  const normalized = String(direction || "").toLowerCase();
  return normalized === "inbound" || normalized === "in";
};
const isMessageFromMe = (message) => {
  if (!message) return false;
  const payload = message?.payload ?? message ?? {};
  if (payload.fromMe || payload.isFromMe || payload.senderIsMe) return true;
  const sender =
    payload?.sender ??
    payload?.message?.sender ??
    payload?.message?.author ??
    payload?.message?.key ??
    null;
  return Boolean(sender?.isMe);
};
const resolveIncomingMessage = (payload) => payload?.message ?? payload ?? {};
const resolveMessageDirection = (message) =>
  message?.direction ??
  message?.payload?.direction ??
  message?.message?.direction;
/* ===========================
 * Helpers
 * =========================== */
const getInitials = (nameOrEmail = "") => {
  const base = (nameOrEmail || "").trim();
  if (!base) return "?";
  const name = base.includes("@") ? base.split("@")[0] : base;
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const resolveCompanyDisplayName = (company) => {
  if (!company) return "";
  return company?.fantasyName || company?.name || "";
};

const resolveCompanyLogoUrl = (company) => {
  if (!company) return "";
  return normalizeAvatarUrl(
    company?.logoUrl ||
      company?.logo ||
      company?.avatarUrl ||
      company?.avatar ||
      company?.imageUrl ||
      company?.image,
  );
};

const UserMenuLabel = ({ user }) => {
  const labelText = user?.name || user?.email || "Usuário";
  const initials = getInitials(user?.name || user?.email);
  const avatarUrl = normalizeAvatarUrl(user?.avatarUrl || user?.avatar);

  return (
    <button
      type="button"
      className="rs-user-btn group relative flex items-center gap-2 rounded-full border border-[#e45412]/20 bg-white/70 px-3 py-1.5 backdrop-blur-md shadow-sm hover:bg-[#e45412]/90 hover:border-[#e45412]/70 transition-colors"
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e45412]/10 text-[#e45412] text-xs font-extrabold shadow-sm group-hover:bg-white group-hover:text-[#e45412]">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="Avatar"
            className="h-full w-full rounded-full object-cover"
          />
        ) : (
          initials
        )}
      </span>
      <span className="max-w-[12rem] truncate text-sm font-semibold text-slate-800 group-hover:text-white">
        {labelText}
      </span>
      <svg
        className="h-4 w-4 text-[#e45412]/70 group-hover:text-white transition-colors"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.127l3.71-3.896a.75.75 0 111.08 1.04l-4.24 4.46a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  );
};

/* ===========================
 * Componente principal
 * =========================== */
export default function TopNavLayout({ children }) {
  const { user, refetchUser, access } = useApplication();
  const location = useLocation();
  const navRef = useRef(null);
  const navIndicatorRef = useRef(null);
  // Abrir/fechar um grupo troca quem leva a marcação (filho ↔ grupo pai).
  const [navGroupTick, setNavGroupTick] = useState(0);
  const bumpNavGroupTick = useCallback(() => setNavGroupTick((t) => t + 1), []);
  useSlidingNavIndicator(
    navRef,
    navIndicatorRef,
    `${location.pathname}|${navGroupTick}`,
  );
  const mobileNavRef = useRef(null);
  const mobileIndicatorRef = useRef(null);

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const isMobileOpenRef = useRef(false);
  isMobileOpenRef.current = isMobileOpen;
  useSlidingNavIndicator(mobileNavRef, mobileIndicatorRef, location.pathname, {
    selector: ".rs-mnav-active",
    extraKey: isMobileOpen,
  });
  const [expandedMobile, setExpandedMobile] = useState({});
  const [isOpenModalAddCredit, setIsOpenModalAddCredit] = useState(false);
  const [titlePage, setTitlePage] = useState("Dashboard");
  const [assignmentToast, setAssignmentToast] = useState(null);
  const lastAssignmentToastKeyRef = useRef("");
  const [automationNotificationToast, setAutomationNotificationToast] =
    useState(null);
  const [showCompanySwitcher, setShowCompanySwitcher] = useState(false);
  const [showMobileCompanySwitcher, setShowMobileCompanySwitcher] =
    useState(false);
  const [switchingCompany, setSwitchingCompany] = useState(null);
  const companySwitcherRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    const saved = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === "true"; // default = false se não tiver salvo
  }); // sidebar mini/expandida
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatTargetUserId, setChatTargetUserId] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  const userAvatarUrl = normalizeAvatarUrl(user?.avatarUrl || user?.avatar);
  const currentUserId = user?.id || user?.userId || user?.responsibleId || null;
  const hasUser = Boolean(currentUserId);
  const socketRef = useSocketRef();
  const socketReady = useSocketReady();

  const { data: pendingTasksCount = 0 } = useQuery(
    ["tasksTodayCount", currentUserId],
    async () => {
      try {
        const todayISO = toISODate(new Date());
        const res = await axios.get(`${CRM.TODAY_TASKS}`, {
          // countOnly: o servidor devolve só o total, em vez da lista inteira.
          params: {
            status: "open",
            date: todayISO,
            responsibleId: currentUserId,
            countOnly: 1,
          },
        });
        const total = Number(res?.data?.count);
        if (Number.isFinite(total)) return Math.max(0, total);
        // Servidor sem suporte a countOnly responde a lista.
        const list = res?.data?.data || [];
        return Array.isArray(list) ? list.length : 0;
      } catch {
        return 0;
      }
    },
    {
      enabled: hasUser,
      staleTime: 60_000,
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    },
  );

  // Compromissos de HOJE que ainda não passaram (badge do Calendário).
  // Regra: eventos com horário contam se começam agora ou no futuro; eventos de
  // dia inteiro contam enquanto o dia não acaba. Eventos que já passaram saem.
  const { data: calendarTodayCount = 0 } = useQuery(
    ["calendarTodayCount", currentUserId],
    async () => {
      try {
        const now = new Date();
        const startOfDay = new Date(now);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(now);
        endOfDay.setHours(23, 59, 59, 999);

        const res = await axios.get(CALENDAR.EVENTS, {
          params: {
            dateFrom: startOfDay.toISOString(),
            dateTo: endOfDay.toISOString(),
          },
        });
        const list = res?.data?.data || [];
        if (!Array.isArray(list)) return 0;

        const nowMs = Date.now();
        return list.filter((ev) => {
          if (ev?.allDay) return true;
          const start = new Date(ev?.startAt);
          if (Number.isNaN(start.getTime())) return false;
          return start.getTime() >= nowMs;
        }).length;
      } catch {
        return 0;
      }
    },
    {
      enabled: hasUser,
      staleTime: 60_000,
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    },
  );

  calculateExpirationPeriodTest(user?.Company?.planExpirationDate);
  const { theme } = useTheme();
  // No escuro a palavra "elinq" (preta) vira creme; o "R" laranja é mantido.
  const logoSrc = theme === "dark" ? "/assets/logo-dark.png" : "/assets/logo.png";

  const company = user?.Company;

  const { data: myCompaniesRaw = [] } = useQuery(
    ["my-companies", user?.id],
    async () => {
      const { data } = await axios.get(FILIAL.MY_COMPANIES);
      return data;
    },
    { enabled: !!user?.id, staleTime: 30_000 },
  );
  // Seletor de empresa/filial: sempre em ordem alfabética.
  const myCompanies = useMemo(
    () => sortCompaniesAlphabetically(myCompaniesRaw),
    [myCompaniesRaw],
  );

  const hasMultipleCompanies = myCompanies.length > 1;

  // JWT holds the switched companyId; DB always returns the original
  const activeCompanyId = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return user?.companyId;
      const payload = decodeJwtPayload(token);
      return payload?.user?.companyId ?? user?.companyId;
    } catch {
      return user?.companyId;
    }
  }, [user?.companyId]);

  const companyLabel = useMemo(() => {
    const active = myCompanies.find((c) => c.id === activeCompanyId);
    if (active) return resolveCompanyDisplayName(active) || active.name;
    return company?.fantasyName || company?.name || "Relinq Sales";
  }, [myCompanies, activeCompanyId, company]);

  const activeCompany = useMemo(() => {
    const active = myCompanies.find((c) => c.id === activeCompanyId);
    if (active) return active;
    return company || null;
  }, [myCompanies, activeCompanyId, company]);

  const activeCompanyLogoUrl = useMemo(
    () => resolveCompanyLogoUrl(activeCompany),
    [activeCompany],
  );

  const isOnWhatsappRoute = location.pathname.startsWith("/whatsapp");

  const {
    data: whatsappUnreadAssignedToMe = 0,
    refetch: refetchWhatsappUnread,
  } = useQuery(
    ["whatsappUnreadAssignedToMe", activeCompanyId, currentUserId],
    async () => {
      if (!activeCompanyId || !currentUserId) return 0;
      try {
        // O backend já filtra por dono e soma tudo. Antes isso era feito aqui,
        // sobre as 20 conversas mais recentes — se a conversa atribuída não
        // caísse nessa janela, o badge ficava em zero.
        const response = await axios.get(
          WHATSAPP.UNREAD_ASSIGNED_COUNT(activeCompanyId),
        );
        const unread = Number(response?.data?.unreadCount);
        return Number.isFinite(unread) ? Math.max(0, unread) : 0;
      } catch {
        return 0;
      }
    },
    {
      // Dentro do WhatsApp o badge fica oculto (valor descartado abaixo), então
      // não há por que consultar; ao sair, o efeito seguinte refaz a consulta.
      enabled: Boolean(activeCompanyId && currentUserId) && !isOnWhatsappRoute,
      staleTime: 30_000,
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
    },
  );

  // Dentro do WhatsApp o badge não faz sentido: o usuário já está vendo as
  // conversas. Ao sair, porém, o valor em cache é anterior às leituras que ele
  // acabou de fazer — sem este refetch o badge reapareceria errado por até 30s.
  const wasOnWhatsappRoute = useRef(isOnWhatsappRoute);
  useEffect(() => {
    if (wasOnWhatsappRoute.current && !isOnWhatsappRoute) {
      refetchWhatsappUnread();
    }
    wasOnWhatsappRoute.current = isOnWhatsappRoute;
  }, [isOnWhatsappRoute, refetchWhatsappUnread]);

  const whatsappUnread = isOnWhatsappRoute ? 0 : whatsappUnreadAssignedToMe;

  const { data: companyAlertsById = {} } = useQuery(
    [
      "companyAlertsById",
      currentUserId,
      myCompanies.map((c) => c.id).join("|"),
    ],
    async () => {
      if (!currentUserId || !myCompanies.length) return {};
      const { data } = await axios.get(FILIAL.MY_COMPANIES_ALERTS);
      return data && typeof data === "object" ? data : {};
    },
    {
      enabled: Boolean(currentUserId && myCompanies.length),
      staleTime: 30_000,
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
    },
  );

  const getCompanyAlertCount = useCallback(
    (companyId) => {
      const total = Number(companyAlertsById?.[companyId]?.total || 0);
      return Number.isFinite(total) ? Math.max(0, total) : 0;
    },
    [companyAlertsById],
  );

  const handleSwitchCompany = async (targetId) => {
    if (targetId === activeCompanyId || switchingCompany) return;
    try {
      setSwitchingCompany(targetId);
      await switchActiveCompany(targetId);
    } catch (error) {
      setSwitchingCompany(null);
      // Sem `await`: não segura o resto do handler até a pessoa fechar o aviso.
      alertDialog({
        title: "Não foi possível trocar de empresa",
        message: describeSwitchCompanyError(error),
        tone: "error",
      });
    }
  };

  const handleOpenGlobalSearch = useCallback(() => {
    window.dispatchEvent(new CustomEvent("global-search:open"));
  }, []);

  useEffect(() => {
    if (!showCompanySwitcher) return;
    const handleClickOutside = (e) => {
      if (
        companySwitcherRef.current &&
        !companySwitcherRef.current.contains(e.target)
      ) {
        setShowCompanySwitcher(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCompanySwitcher]);

  const menuLinks = useMemo(
    () => [
      {
        name: "Visão Geral",
        link: "/dashboard",
        icon: "grading",
        perm: null,
        isCollapsible: false,
      },
      {
        name: "CRM",
        link: "/board",
        icon: "dashboard",
        perm: "crm.funnel.view",
        isCollapsible: false,
      },
      {
        name: "Tarefas",
        link: "/crm/tasks",
        icon: "tasks",
        perm: "crm.task.view",
        isCollapsible: false,
      },
      {
        name: "Calendário",
        link: "/calendario",
        icon: "calendar_month",
        perm: "calendar.view",
        isCollapsible: false,
      },
      {
        name: "Agendamento",
        link: "/agendamentos",
        icon: "event_available",
        perm: "calendar.booking.manage",
        isCollapsible: false,
      },
      // O grupo segue o CICLO DE VIDA do contato, nesta ordem: onde ele mora →
      // como achar mais → quais priorizar → como trabalhar. Cadências entrou
      // aqui, e não como item solto no topo, porque uma cadência não é uma
      // área do produto: é o que se FAZ com um contato depois de priorizá-lo.
      {
        name: "Contatos",
        icon: "contact_page",
        perm: "contacts.lead.view",
        isCollapsible: true,
        children: [
          {
            name: "Lista de Contatos",
            link: "/contacts",
            icon: "contact_page",
            perm: "contacts.lead.view",
          },
          {
            name: "Encontrar oportunidades",
            link: "/leads/generator",
            icon: "person_search",
            perm: "contacts.generator.use",
          },
          {
            name: "Pontuação",
            link: "/contacts/pontuacao",
            icon: "speed",
            perm: "scoring.view",
          },
          {
            name: "Cadências",
            link: "/cadencias",
            icon: "repeat",
            perm: "cadence.view",
          },
        ],
      },
      {
        name: "Catálogo",
        icon: "storefront",
        perm: ["catalog.product.view", "catalog.budget.view"],
        isCollapsible: true,
        children: [
          {
            name: "Catálogo de produtos",
            link: "/products",
            icon: "inventory_2",
            perm: "catalog.product.view",
          },
          {
            name: "Orçamentos",
            link: "/budgets",
            icon: "request_quote",
            perm: "catalog.budget.view",
          },
        ],
      },
      {
        name: "Desempenho",
        icon: "insights",
        perm: ["reports.view", "goals.view", "crm.card.view"],
        isCollapsible: true,
        children: [
          {
            name: "Relatórios",
            link: "/reports",
            icon: "assessment",
            perm: "reports.view",
          },
          {
            name: "Escritório Virtual",
            link: "/desempenho/escritorio",
            icon: "sports_esports",
            perm: "reports.view",
          },
          {
            name: "VoIP",
            link: "/desempenho/ligacoes",
            icon: "call",
            perm: "crm.card.view",
          },
          {
            name: "Metas",
            link: "/metas",
            icon: "flag",
            perm: "goals.view",
          },
        ],
      },
      {
        name: "Gestão",
        icon: "manage_accounts",
        perm: ["settings.company.manage", "settings.team.manage", "settings.user.manage", "settings.role.manage", "api.key.manage", "api.webhook.manage"],
        isCollapsible: true,
        children: [
          {
            name: "Empresa",
            link: "/empresa",
            icon: "business",
            perm: "settings.company.manage",
          },
          {
            name: "Times",
            link: "/teams",
            icon: "groups",
            perm: "settings.team.manage",
          },
          {
            name: "Usuários e permissões",
            link: "/users",
            icon: "group",
            perm: ["settings.user.manage", "settings.role.manage"],
          },
          {
            name: "Desenvolvedor",
            link: "/desenvolvedor",
            icon: "code",
            perm: ["api.key.manage", "api.webhook.manage"],
          },
        ],
      },
      // 👉 último item: Mensagens como ação que abre o dock
      {
        name: "Whatsapp",
        link: "/whatsapp",
        icon: "whatsapp",
        perm: "whatsapp.chat.access",
        isCollapsible: false,
      },
      {
        name: "O Estagiário",
        link: "/insights",
        icon: "hail",
        perm: "ai.insights.view",
        isCollapsible: false,
        highlight: true,
      },
    ],
    [setIsChatOpen],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      SIDEBAR_COLLAPSED_KEY,
      isCollapsed ? "true" : "false",
    );
  }, [isCollapsed]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onOpenDm = (event) => {
      const targetUserId = Number(event?.detail?.userId || 0);
      if (!targetUserId) return;
      setChatTargetUserId(targetUserId);
      setIsChatOpen(true);
    };

    window.addEventListener("global-chat:open-dm", onOpenDm);
    return () => window.removeEventListener("global-chat:open-dm", onOpenDm);
  }, []);

  useEffect(() => {
    const flat = [];
    menuLinks.forEach((m) => {
      flat.push({ name: m.name, link: m.link });
      (m.children || []).forEach((c) =>
        flat.push({ name: c.name, link: c.link }),
      );
    });
    // A raiz "/" abre o mesmo Dashboard de "/dashboard" (Visão Geral).
    const currentPath = location.pathname === "/" ? "/dashboard" : location.pathname;
    const found = flat.find((f) => f.link === currentPath);
    setTitlePage(found?.name || "Dashboard");
  }, [location.pathname, menuLinks]);

  // Menu por chave do cargo (RBAC): cada item declara `perm` (chave, lista "qualquer
  // uma" ou `null` = todos). Quem é da equipe da plataforma (POWER_USER) vê tudo.
  // Enquanto as permissões não chegaram, `hasAnyPermission` libera (o menu não
  // pisca; o servidor recusa o que não for permitido).
  const hasPerm = (item) => {
    if (item.perm === null) return true;
    return hasAnyPermission(access, item.perm);
  };
  const canAccessIntegrations = hasPermission(
    access,
    "settings.integration.manage",
  );

  // Assinatura/pagamento e chat interno: chave do cargo (o servidor recusa o
  // resto). Enquanto /me/access não chega, `hasPermission` libera (sem piscar).
  const canManageBilling = hasPermission(access, "settings.billing.manage");
  const canUseInternalChat = hasPermission(access, "chat.internal.access");

  const topLevel = useMemo(
    () =>
      menuLinks.filter((item) => {
        if (!hasPerm(item)) return false;
        // Com as permissões carregadas, grupo sem nenhum item liberado some.
        if (
          access &&
          Array.isArray(item.children) &&
          item.children.length > 0 &&
          !item.children.some(hasPerm)
        ) {
          return false;
        }
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuLinks, access],
  );
  // "/" é a Visão Geral (a rota raiz renderiza o mesmo Dashboard): ao carregar o
  // sistema sem caminho, esse item já aparece selecionado.
  const isActive = (href) =>
    location.pathname === href ||
    (href === "/dashboard" && location.pathname === "/");

  const toggleMobileGroup = (name) =>
    setExpandedMobile((p) => ({ ...p, [name]: !p[name] }));

  const handleRefresh = () => refetchUser();

  // Fecha a gaveta do celular ao navegar — com um respiro para o contorno do
  // item ativo terminar de deslizar até o item tocado.
  useEffect(() => {
    if (!isMobileOpenRef.current) return undefined;
    const timer = setTimeout(() => setIsMobileOpen(false), 380);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const nextExpanded = {};
    topLevel.forEach((item) => {
      if (!item?.isCollapsible) return;
      const hasActiveChild = (item.children || []).some(
        (child) => child?.link && location.pathname === child.link,
      );
      if (hasActiveChild) {
        nextExpanded[item.name] = true;
      }
    });

    if (!Object.keys(nextExpanded).length) return;
    setExpandedMobile((prev) => ({ ...prev, ...nextExpanded }));
  }, [location.pathname, topLevel]);

  useEffect(() => {
    if (!socketReady || !socketRef.current) return;
    const socket = socketRef.current;
    const handleWhatsappMessage = (payload) => {
      const message = resolveIncomingMessage(payload);
      const direction = resolveMessageDirection(message);
      if (!isInboundDirection(direction)) return;
      if (isMessageFromMe(message)) return;
      refetchWhatsappUnread();
    };

    socket.on("whatsapp:new-message", handleWhatsappMessage);
    socket.on("whatsapp:conversation-assigned", refetchWhatsappUnread);
    return () => {
      socket.off("whatsapp:new-message", handleWhatsappMessage);
      socket.off("whatsapp:conversation-assigned", refetchWhatsappUnread);
    };
  }, [socketReady, socketRef, refetchWhatsappUnread]);

  useEffect(() => {
    if (!socketReady || !socketRef.current) return;
    const socket = socketRef.current;
    const handleNotificationNew = (notification) => {
      if (!notification) return;
      if (notification.type !== "whatsapp_conversation_assigned") return;
      const targetUrl =
        typeof notification.url === "string" && notification.url.trim()
          ? notification.url.trim()
          : "/whatsapp";
      const dedupeKey = `${notification.type || ""}:${notification.entityId || ""}:${targetUrl}`;
      if (lastAssignmentToastKeyRef.current === dedupeKey) return;
      lastAssignmentToastKeyRef.current = dedupeKey;
      setAssignmentToast({
        id: notification.id || Date.now(),
        title: notification.title || "Nova conversa atribuída",
        message:
          notification.body ||
          "Uma nova conversa do WhatsApp foi atribuída para você.",
        url: targetUrl,
      });
    };

    socket.on("notification:new", handleNotificationNew);

    const handleConversationAssigned = (payload) => {
      const conversation = payload?.conversation || {};
      const assignedUserId =
        conversation?.assignedUserId ?? payload?.assignedUserId ?? null;
      if (!assignedUserId || String(assignedUserId) !== String(currentUserId)) {
        return;
      }

      const conversationKey =
        payload?.conversationKey ||
        payload?.contact ||
        conversation?.conversationKey ||
        conversation?.contact ||
        conversation?.id ||
        null;

      const targetUrl = conversationKey
        ? `/whatsapp?conversationKey=${encodeURIComponent(String(conversationKey))}`
        : "/whatsapp";
      const dedupeKey = `event:${String(conversationKey || "")}:${targetUrl}`;
      if (lastAssignmentToastKeyRef.current === dedupeKey) return;
      lastAssignmentToastKeyRef.current = dedupeKey;

      setAssignmentToast({
        id: Date.now(),
        title: "Nova conversa atribuída a você",
        message: conversationKey
          ? `A conversa ${String(conversationKey)} foi atribuída para seu atendimento.`
          : "Uma nova conversa do WhatsApp foi atribuída para você.",
        url: targetUrl,
      });
    };

    socket.on("whatsapp:conversation-assigned", handleConversationAssigned);

    const handleAutomationNotification = (payload) => {
      if (!payload) return;
      const message = String(payload.message || "").trim();
      const cardTitle = String(payload.cardTitle || "").trim();
      const automationName = String(payload.automationName || "").trim();
      setAutomationNotificationToast({
        id: Date.now(),
        title: automationName
          ? automationName
          : "Notificação automática",
        message:
          message ||
          (cardTitle
            ? `Ação disparada no card "${cardTitle}".`
            : "Uma automação disparou uma notificação para você."),
        cardId: payload.cardId || null,
      });
    };

    socket.on("automation:notification", handleAutomationNotification);

    return () => {
      socket.off("notification:new", handleNotificationNew);
      socket.off("whatsapp:conversation-assigned", handleConversationAssigned);
      socket.off("automation:notification", handleAutomationNotification);
    };
  }, [socketReady, socketRef, currentUserId]);

  return (
    <>
      <GlobalSearchPalette />
      <div className="rs-shell h-screen w-full bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 flex overflow-hidden">
        {/* ========== SIDEBAR DESKTOP ========== */}
        <motion.aside
          layout
          initial={false}
          animate={{ width: isCollapsed ? 76 : 248 }} // 76px ~ colapsado, 288px ~ aberto
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className="rs-aside hidden lg:flex flex-col border-r border-slate-200/70 bg-gradient-to-b from-white via-slate-50 to-slate-100 backdrop-blur-sm shadow-[4px_0_18px_rgba(15,23,42,0.06)]"
        >
          {/* Logo + toggle */}
          <div className="flex items-center justify-between px-3.5 py-2 border-b border-slate-100/80">
            <Link
              to="/dashboard"
              className="flex items-center gap-3 overflow-hidden"
            >
              <AnimatePresence initial={false}>
                {!isCollapsed ? (
                  <motion.div
                    key="logo-full"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center gap-2"
                  >
                    <div className="rounded-2xl px-2 py-1 flex items-center gap-2 shadow-sm">
                      <img
                        src={logoSrc}
                        alt="Logo"
                        className="h-7 w-auto object-contain"
                      />
                      <span className="text-[11px] font-semibold tracking-wide text-gray-600 uppercase">
                        Sales
                      </span>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </Link>

            <button
              type="button"
              onClick={() => setIsCollapsed((prev) => !prev)}
              className="rs-collapse-btn flex items-center justify-center rounded-full border border-slate-200 bg-white h-12 w-12 min-w-12 text-slate-500 hover:text-[#e45412] hover:border-[#e45412]/40 hover:bg-[#e45412]/5 transition"
              title={isCollapsed ? "Expandir menu" : "Recolher menu"}
            >
              {isCollapsed ? (
                <img className="h-4 w-4" src={"/assets/logo_mobile.png"} />
              ) : null}
              <motion.svg
                className="h-3.5 w-3.5 min-w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                animate={{ rotate: isCollapsed ? 0 : 180 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
              >
                <path
                  d="M10 6L16 12L10 18"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </button>
          </div>

          {/* Navegação principal */}
          <nav
            ref={navRef}
            className="relative mt-4 flex-1 overflow-y-auto px-2.5 pb-4"
          >
            <div ref={navIndicatorRef} className="rs-nav-indicator" aria-hidden="true" />
            <div className="space-y-2">
              {topLevel.map((item) => {
                if (item.type === "action") {
                  return (
                    <DesktopNavActionItem
                      key={item.name}
                      icon={item.icon}
                      label={item.name}
                      collapsed={isCollapsed}
                      onClick={item.onClick}
                      hasUnread={item.name === "Mensagens" && chatUnread > 0}
                    />
                  );
                }

                return !item.isCollapsible ? (
                  <DesktopNavItem
                    key={item.name}
                    to={item.link}
                    icon={item.icon}
                    label={item.name}
                    active={isActive(item.link)}
                    collapsed={isCollapsed}
                    highlight={item.highlight}
                    badgeCount={
                      item.name === "Tarefas"
                        ? pendingTasksCount
                        : item.link === "/calendario"
                          ? calendarTodayCount
                          : item.link === "/whatsapp"
                            ? whatsappUnread
                            : 0
                    }
                  />
                ) : (
                  <DesktopNavGroup
                    key={item.name}
                    label={item.name}
                    icon={item.icon}
                    items={(item.children || []).filter(hasPerm)}
                    isActiveGroup={(item.children || []).some((c) =>
                      isActive(c.link),
                    )}
                    activeLink={location.pathname}
                    collapsed={isCollapsed}
                    onOpenChange={bumpNavGroupTick}
                  />
                );
              })}
            </div>
          </nav>

          {/* Company switcher na base */}
          <div
            className="rs-company relative border-t border-gray-300 bg-gray-100"
            ref={companySwitcherRef}
          >
            <button
              type="button"
              onClick={() =>
                hasMultipleCompanies && setShowCompanySwitcher((v) => !v)
              }
              className={[
                "rs-company-btn w-full flex items-center gap-2 px-3 py-3 text-left transition-colors",
                hasMultipleCompanies
                  ? "hover:bg-gray-200 cursor-pointer"
                  : "cursor-default",
              ].join(" ")}
            >
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600 text-xs font-bold">
                {activeCompanyLogoUrl ? (
                  <img
                    src={activeCompanyLogoUrl}
                    alt={companyLabel}
                    className="h-full w-full rounded-lg object-contain"
                  />
                ) : (
                  getInitials(companyLabel)
                )}
              </span>
              {!isCollapsed && (
                <>
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="text-sm font-semibold text-slate-700 truncate">
                      {companyLabel}
                    </span>
                  </span>
                  {hasMultipleCompanies && (
                    <IoIosArrowDown
                      className={[
                        "h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200",
                        showCompanySwitcher ? "rotate-180" : "",
                      ].join(" ")}
                    />
                  )}
                </>
              )}
            </button>

            {showCompanySwitcher && hasMultipleCompanies && (
              <div className="rs-panel absolute bottom-full left-2 right-2 mb-1 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden z-50">
                <p className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Trocar empresa
                </p>
                {myCompanies.map((c) => {
                  const isActive = c.id === activeCompanyId;
                  const isLoading = switchingCompany === c.id;
                  const companyName = resolveCompanyDisplayName(c) || c.name;
                  const companyLogoUrl = resolveCompanyLogoUrl(c);
                  const companyAlertCount = getCompanyAlertCount(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      disabled={isActive || !!switchingCompany}
                      onClick={() => handleSwitchCompany(c.id)}
                      className={[
                        "rs-company-opt w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors",
                        isActive
                          ? "rs-company-opt-active bg-orange-50 cursor-default"
                          : "hover:bg-slate-50 cursor-pointer",
                      ].join(" ")}
                    >
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600 text-xs font-bold">
                        {companyLogoUrl ? (
                          <img
                            src={companyLogoUrl}
                            alt={companyName}
                            className="h-full w-full rounded-lg object-contain"
                          />
                        ) : (
                          getInitials(companyName)
                        )}
                      </span>
                      <span className="flex-1 text-sm text-slate-700 truncate font-medium">
                        {companyName}
                      </span>
                      {!isActive && companyAlertCount > 0 && (
                        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                          {companyAlertCount > 99 ? "99+" : companyAlertCount}
                        </span>
                      )}
                      {isLoading && (
                        <svg
                          className="h-3.5 w-3.5 animate-spin text-orange-500 shrink-0"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                      )}
                      {isActive && !isLoading && (
                        <svg
                          className="h-3.5 w-3.5 text-orange-500 shrink-0"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </motion.aside>

        {/* ========== MOBILE SIDEBAR (overlay) ========== */}
        <AnimatePresence>
          {isMobileOpen && (
            <motion.div
              className="lg:hidden fixed inset-0"
              style={{ zIndex: 9999 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <button
                type="button"
                className="rs-backdrop absolute inset-0 bg-black/35"
                onClick={() => setIsMobileOpen(false)}
              />

              <motion.aside
                initial={{ x: -260, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -260, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="absolute left-0 top-0 h-full w-72 max-w-[88vw] bg-orange-600 text-white shadow-2xl flex flex-col"
              >
                <div className="px-4 pt-4 pb-3 border-b border-white/10 flex items-center justify-between gap-3 bg-black/10">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                      {userAvatarUrl ? (
                        <img
                          src={userAvatarUrl}
                          alt="Avatar"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-sm font-bold text-white">
                          {getInitials(user?.name || user?.email)}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold truncate max-w-[10rem]">
                        {user?.name || "Usuário"}
                      </span>
                      <span className="text-xs text-white/70 truncate max-w-[12rem]">
                        {user?.email}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 p-1.5"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div
                  ref={mobileNavRef}
                  className="relative flex-1 overflow-y-auto px-3 py-3"
                >
                  <div
                    ref={mobileIndicatorRef}
                    className="rs-nav-indicator rs-nav-indicator--mobile"
                    aria-hidden="true"
                  />
                  {topLevel.map((item) =>
                    item.type === "action" ? (
                      <MobileActionItem
                        key={item.name}
                        icon={item.icon}
                        label={item.name}
                        onClick={() => {
                          item.onClick?.();
                          setIsMobileOpen(false);
                        }}
                        hasUnread={item.name === "Mensagens" && chatUnread > 0}
                      />
                    ) : !item.isCollapsible ? (
                      <MobileItem
                        key={item.name}
                        to={item.link}
                        icon={item.icon}
                        active={isActive(item.link)}
                        label={item.name}
                        badgeCount={
                          item.name === "Tarefas"
                            ? pendingTasksCount
                            : item.link === "/calendario"
                              ? calendarTodayCount
                              : item.link === "/whatsapp"
                                ? whatsappUnread
                                : 0
                        }
                      />
                    ) : (
                      <MobileGroup
                        key={item.name}
                        label={item.name}
                        icon={item.icon}
                        open={!!expandedMobile[item.name]}
                        onToggle={() => toggleMobileGroup(item.name)}
                        childrenItems={(item.children || []).filter(hasPerm)}
                        isActiveGroup={(item.children || []).some((c) =>
                          isActive(c.link),
                        )}
                      />
                    ),
                  )}
                </div>

                {hasMultipleCompanies && (
                  <div className="border-t border-white/10 bg-black/20">
                    <button
                      type="button"
                      onClick={() => setShowMobileCompanySwitcher((v) => !v)}
                      className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-white/10 transition-colors"
                    >
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/15 text-white text-xs font-bold">
                        {activeCompanyLogoUrl ? (
                          <img
                            src={activeCompanyLogoUrl}
                            alt={companyLabel}
                            className="h-full w-full rounded-lg object-cover"
                          />
                        ) : (
                          getInitials(companyLabel)
                        )}
                      </span>
                      <span className="flex-1 text-sm font-semibold text-white truncate">
                        <span className="inline-flex min-w-0 items-center gap-2">
                          <span className="truncate">{companyLabel}</span>
                        </span>
                      </span>
                      <IoIosArrowDown
                        className={[
                          "h-4 w-4 shrink-0 text-white/60 transition-transform duration-200",
                          showMobileCompanySwitcher ? "rotate-180" : "",
                        ].join(" ")}
                      />
                    </button>

                    {showMobileCompanySwitcher && (
                      <div className="px-3 pb-2 space-y-1">
                        {myCompanies.map((c) => {
                          const isActive = c.id === activeCompanyId;
                          const isLoading = switchingCompany === c.id;
                          const companyName =
                            resolveCompanyDisplayName(c) || c.name;
                          const companyLogoUrl = resolveCompanyLogoUrl(c);
                          const companyAlertCount = getCompanyAlertCount(c.id);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              disabled={isActive || !!switchingCompany}
                              onClick={() => handleSwitchCompany(c.id)}
                              className={[
                                "w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-left transition-colors",
                                isActive
                                  ? "bg-white/20 cursor-default"
                                  : "hover:bg-white/10 cursor-pointer",
                              ].join(" ")}
                            >
                              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-white/15 text-white text-[10px] font-bold">
                                {companyLogoUrl ? (
                                  <img
                                    src={companyLogoUrl}
                                    alt={companyName}
                                    className="h-full w-full rounded-lg object-cover"
                                  />
                                ) : (
                                  getInitials(companyName)
                                )}
                              </span>
                              <span className="flex-1 text-sm text-white/90 truncate font-medium">
                                {companyName}
                              </span>
                              {!isActive && companyAlertCount > 0 && (
                                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                                  {companyAlertCount > 99
                                    ? "99+"
                                    : companyAlertCount}
                                </span>
                              )}
                              {isLoading && (
                                <svg
                                  className="h-3.5 w-3.5 animate-spin text-white/70 shrink-0"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8H4z"
                                  />
                                </svg>
                              )}
                              {isActive && !isLoading && (
                                <svg
                                  className="h-3.5 w-3.5 text-white shrink-0"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.704 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div className="border-t border-white/10 px-4 py-3 space-y-1 bg-black/15">
                  <Link
                    to="/profile"
                    className="block text-sm py-1 text-white/90 hover:text-white hover:underline"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    Meu perfil
                  </Link>
                  {canManageBilling && (
                    <Link
                      to="/assinatura"
                      className="block text-sm py-1 text-white/90 hover:text-white hover:underline"
                      onClick={() => setIsMobileOpen(false)}
                    >
                      Gerenciar assinatura
                    </Link>
                  )}

                  {canAccessIntegrations && (
                    <Link
                      to="/integracoes"
                      className="block text-sm py-1 text-white/90 hover:text-white hover:underline"
                      onClick={() => setIsMobileOpen(false)}
                    >
                      Integrações
                    </Link>
                  )}

                  <Link
                    to="/mensagens"
                    className="block text-sm py-1 text-white/90 hover:text-white hover:underline"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    Disparo de mensagem
                  </Link>

                  <div className="flex items-center justify-between py-1 text-sm text-white/90">
                    <span>Tema escuro</span>
                    <ThemeToggle />
                  </div>

                  <a
                    target="_blank"
                    href="https://wa.me/+5515998547194"
                    rel="noreferrer"
                    className="block text-sm py-1 text-white/90 hover:text-white hover:underline"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    Entre em contato
                  </a>
                  <button
                    type="button"
                    className="mt-1 w-full text-left text-sm font-semibold text-red-100 hover:text-white"
                    onClick={removeToken}
                  >
                    Sair
                  </button>
                </div>
              </motion.aside>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ========== CONTEÚDO PRINCIPAL ========== */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header topo */}
          <header className="rs-header sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-100 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
            <div className="flex items-center gap-3 px-3 md:px-6 py-2.5">
              {/* Esquerda: botão mobile + título */}
              <div className="flex min-w-0 items-center gap-3 md:shrink-0">
                {/* burger mobile */}
                <button
                  className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:text-[#e45412] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#e45412]/40"
                  onClick={() => setIsMobileOpen((o) => !o)}
                  aria-label="Abrir menu"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 6h16M4 12h16M4 18h16"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>

                <div className="flex min-w-0 flex-col">
                  <div className="flex items-center gap-2">
                    <h1 className="truncate text-sm md:text-base font-semibold text-slate-900">
                      {titlePage}
                    </h1>
                  </div>
                  <p className="text-[11px] text-slate-400 hidden sm:block">
                    Bem-vindo(a) de volta,{" "}
                    <span className="font-medium text-slate-500">
                      {user?.name || "usuário"}
                    </span>
                    .
                  </p>
                </div>
              </div>

              <div className="hidden md:flex flex-1 items-center justify-center min-w-0">
                <button
                  type="button"
                  onClick={handleOpenGlobalSearch}
                  className="rs-search flex h-11 w-full max-w-2xl items-center gap-3 rounded-full border border-slate-200 bg-white px-4 text-left text-slate-400 shadow-sm transition hover:border-[#e45412]/40 hover:bg-[#e45412]/5 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#e45412]/20"
                  aria-label="O que você procura?"
                >
                  <HiSearch className="h-4.5 w-4.5 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1 truncate text-sm">
                    Tecle / para pesquisar...
                  </span>
                </button>
              </div>

              {/* Direita: status conta + notificações + user menu */}
              <div className="ml-auto flex items-center gap-2 md:gap-3 shrink-0">
                {/* No celular a barra de busca acima some (hidden md:flex) e o
                    atalho "/" não existe — sem este botão não havia NENHUM
                    caminho para a busca de contatos/cards no mobile. */}
                <button
                  type="button"
                  onClick={handleOpenGlobalSearch}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-orange-400 transition hover:bg-orange-50 md:hidden"
                  aria-label="Buscar contatos, cards e tarefas"
                  title="Buscar"
                >
                  <HiSearch className="h-6 w-6" />
                </button>
                {canUseInternalChat && (
                  <div className="relative">
                    <HiChatAlt
                      fontSize={25}
                      onClick={() => {
                        setChatTargetUserId(null);
                        setIsChatOpen(true);
                      }}
                      className="rs-icon-btn cursor-pointer text-orange-400"
                    />
                    {chatUnread > 0 && (
                      <span className="absolute -top-2 -right-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow">
                        {chatUnread > 9 ? "9+" : chatUnread}
                      </span>
                    )}
                  </div>
                )}
                <NotificationBell />
                <div className="hidden sm:block">
                  <Dropdown
                    label={<UserMenuLabel user={user} />}
                    arrowIcon={false}
                    inline
                  >
                    <Dropdown.Header>
                      <span className="block text-sm font-medium truncate">
                        {user?.email}
                      </span>
                    </Dropdown.Header>

                    <Dropdown.Item className="py-0 px-0">
                      <Link
                        to="/profile"
                        className="block w-full py-2 px-4 text-left text-sm"
                      >
                        Meu perfil
                      </Link>
                    </Dropdown.Item>

                    {canManageBilling && (
                      <Dropdown.Item className="py-0 px-0">
                        <Link
                          to="/assinatura"
                          className="block w-full py-2 px-4 text-left text-sm"
                        >
                          Gerenciar assinatura
                        </Link>
                      </Dropdown.Item>
                    )}

                    <Dropdown.Divider />

                    {canAccessIntegrations && (
                      <Dropdown.Item className="py-0 px-0">
                        <Link
                          to="/integracoes"
                          className="block w-full py-2 px-4 text-left text-sm"
                        >
                          Integrações
                        </Link>
                      </Dropdown.Item>
                    )}

                    <Dropdown.Item className="py-0 px-0">
                      <Link
                        to="/mensagens"
                        className="block w-full py-2 px-4 text-left text-sm"
                      >
                        Disparo de mensagem
                      </Link>
                    </Dropdown.Item>

                    {/* Não é Dropdown.Item de propósito: clicar na chave não
                        fecha o menu. */}
                    <li
                      role="none"
                      className="flex items-center justify-between gap-6 px-4 py-2 text-sm text-gray-700"
                    >
                      <span>Tema escuro</span>
                      <ThemeToggle />
                    </li>

                    <Dropdown.Divider />

                    <Dropdown.Item className="py-0 px-0">
                      <a
                        target="_blank"
                        href="https://wa.me/+5515998547194"
                        rel="noreferrer"
                        className="block w-full py-2 px-4 text-left text-sm"
                      >
                        Entre em contato
                      </a>
                    </Dropdown.Item>

                    <Dropdown.Divider />

                    <Dropdown.Item onClick={removeToken}>Sair</Dropdown.Item>
                  </Dropdown>
                </div>
              </div>
            </div>
          </header>

          {/* Main content area */}
          <main className="rs-main flex-1 overflow-y-auto px-3 md:px-6 pt-3 pb-24 md:py-4 overflow-x-hidden">
            {children}
          </main>
        </div>
      </div>

      {/* Dock de chat global, alinhado com a sidebar */}
      {canUseInternalChat && (
        <GlobalChatDock
          sidebarCollapsed={isCollapsed}
          open={isChatOpen}
          targetUserId={chatTargetUserId}
          onClose={() => {
            setIsChatOpen(false);
            setChatTargetUserId(null);
          }}
          onUnreadChange={setChatUnread}
        />
      )}

      <Tooltip
        id="sidebar-tooltip"
        place="right"
        offset={10}
        style={{
          backgroundColor: theme === "dark" ? "#38344a" : "#0f172a",
          color: theme === "dark" ? "#F4F2F8" : "#f9fafb",
          border: theme === "dark" ? "1px solid #46415A" : undefined,
          fontSize: "11px",
          padding: "6px 8px",
          borderRadius: "999px",
          zIndex: "10000",
        }}
      />

      <AnimatedToast
        open={Boolean(assignmentToast)}
        title={assignmentToast?.title || "Notificação"}
        message={assignmentToast?.message || ""}
        duration={6000}
        onClose={() => setAssignmentToast(null)}
      />
      <AnimatedToast
        open={Boolean(automationNotificationToast)}
        title={
          automationNotificationToast?.title || "Notificação automática"
        }
        message={automationNotificationToast?.message || ""}
        duration={8000}
        onClose={() => setAutomationNotificationToast(null)}
      />
    </>
  );
}

/* ===========================
 * Subcomponentes de navegação
 * =========================== */

function SidebarIcon({ icon, className, style }) {
  if (icon === "whatsapp") {
    return <FaWhatsapp className={className} style={style} />;
  }
  if (icon === "tasks") {
    return <TaskIcon className={className} style={style} />;
  }
  return (
    <Icon fontSize="inherit" className={className} style={style}>
      {icon}
    </Icon>
  );
}

/**
 * Contorno do item ativo do menu que DESLIZA até o item clicado.
 *
 * Os itens ativos continuam marcados com `.rs-nav-active` (cor do texto, etc.);
 * quando este indicador está funcionando, o `Sidebar.css` zera só o fundo/borda
 * deles (`.rs-nav-slide`) e o `.rs-nav-indicator` — um único elemento absoluto
 * dentro do `<nav>` — desenha o contorno e se move por `transform`. Sem
 * JavaScript/ResizeObserver o item continua com o contorno próprio de antes.
 */
function useSlidingNavIndicator(
  navRef,
  indicatorRef,
  routeKey,
  { selector = ".rs-nav-active", extraKey = null } = {},
) {
  const lastKeyRef = useRef("");

  const place = useCallback(
    (animate) => {
      const nav = navRef.current;
      const indicator = indicatorRef.current;
      if (!nav || !indicator) return;

      const active = nav.querySelector(selector);
      if (!active) {
        // Rota que não está no menu (ex.: perfil): o contorno some.
        indicator.style.opacity = "0";
        nav.classList.remove("rs-nav-slide");
        lastKeyRef.current = "";
        return;
      }
      nav.classList.add("rs-nav-slide");

      const navRect = nav.getBoundingClientRect();
      const rect = active.getBoundingClientRect();
      const x = rect.left - navRect.left + nav.scrollLeft;
      const y = rect.top - navRect.top + nav.scrollTop;
      const radius = window.getComputedStyle(active).borderRadius;
      const key = [x, y, rect.width, rect.height, radius].join("|");
      if (key === lastKeyRef.current) return;

      const reduceMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const instant =
        !animate || reduceMotion || indicator.style.opacity !== "1";

      if (instant) indicator.style.transition = "none";
      indicator.style.transform = `translate(${x}px, ${y}px)`;
      indicator.style.width = `${rect.width}px`;
      indicator.style.height = `${rect.height}px`;
      indicator.style.borderRadius = radius;
      indicator.style.opacity = "1";
      lastKeyRef.current = key;
      if (instant) {
        // força o reflow para o "sem transição" valer antes de reativá-la
        void indicator.offsetWidth;
        indicator.style.transition = "";
      }
    },
    [navRef, indicatorRef, selector],
  );

  // Troca de rota: desliza. `extraKey` refaz a medição quando o menu inteiro é
  // (re)montado — a gaveta do celular só existe no DOM enquanto está aberta.
  useLayoutEffect(() => {
    if (extraKey !== null) lastKeyRef.current = "";
    place(true);
  }, [routeKey, extraKey, place]);

  // Mudanças de tamanho (recolher o menu, grupo abrindo, itens que chegam
  // depois): só reposiciona, sem animar, para o contorno acompanhar o item.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => place(false));
    observer.observe(nav);
    Array.from(nav.children).forEach((child) => {
      if (child !== indicatorRef.current) observer.observe(child);
    });
    return () => observer.disconnect();
  }, [navRef, indicatorRef, place, extraKey]);
}

function DesktopNavItem({
  to,
  icon,
  label,
  active,
  collapsed,
  highlight,
  badgeCount,
}) {
  const tooltipProps = collapsed
    ? {
        "data-tooltip-id": "sidebar-tooltip",
        "data-tooltip-content": label,
      }
    : {};
  const showBadge = Number(badgeCount) > 0;

  const baseClasses = active
    ? "rs-nav-active bg-gradient-to-r from-[#e45412]/14 via-[#e45412]/5 to-transparent text-[#e45412] border border-[#e45412]/30 shadow-sm"
    : highlight
      ? "rs-nav text-[#f97316]"
      : "rs-nav text-slate-600 hover:bg-slate-50 hover:text-[#e45412] border border-transparent";

  return (
    <Link
      to={to}
      {...tooltipProps}
      className={`group flex items-center gap-1 rounded-2xl px-2 py-1 text-sm transition-all mb-0.5
        ${baseClasses} ${collapsed ? "justify-center px-1" : ""}`}
    >
      {icon && (
        <div
          className={`relative flex h-8 w-8 items-center justify-center rounded-xl text-[22px] transition-colors ${
            highlight ? "highlight-icon" : ""
          }`}
        >
          <SidebarIcon icon={icon} className="text-[22px]" />
          {showBadge && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold flex items-center justify-center ring-2 ring-white">
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </div>
      )}
      {!collapsed && <span className="font-medium truncate">{label}</span>}
    </Link>
  );
}

function DesktopNavActionItem({ icon, label, collapsed, onClick, hasUnread }) {
  const tooltipProps = collapsed
    ? {
        "data-tooltip-id": "sidebar-tooltip",
        "data-tooltip-content": label,
      }
    : {};

  return (
    <button
      type="button"
      onClick={onClick}
      {...tooltipProps}
      className={`group flex items-center gap-1 rounded-2xl px-2 py-1 text-sm mb-0.5 transition-all
      rs-nav text-slate-600 hover:text-[#e45412] hover:bg-slate-50 border border-transparent w-full ${
        collapsed ? "justify-center px-1" : ""
      }`}
    >
      {icon && (
        <div className="relative flex h-8 w-8 items-center justify-center rounded-xl border-slate-200 text-[22px] text-slate-500 group-hover:border-[#e45412]/40 group-hover:bg-[#e45412]/5 group-hover:text-[#e45412] transition-colors">
          <SidebarIcon icon={icon} className="text-[22px]" />
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
          )}
        </div>
      )}
      {!collapsed && <span className="font-medium truncate pr-1">{label}</span>}
    </button>
  );
}

function DesktopNavGroup({
  label,
  icon,
  items,
  isActiveGroup,
  activeLink,
  collapsed,
  onOpenChange,
}) {
  const [open, setOpen] = useState(isActiveGroup);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const portalRef = useRef(null);
  const [btnPos, setBtnPos] = useState(null);

  useEffect(() => {
    if (isActiveGroup) setOpen(true);
  }, [isActiveGroup]);

  // Avisa o menu para reposicionar o contorno do item ativo (filho aberto ou
  // grupo recolhido).
  useLayoutEffect(() => {
    onOpenChange?.();
  }, [open, onOpenChange]);

  useEffect(() => {
    const onClick = (e) => {
      const inTrigger = ref.current?.contains(e.target);
      const inPortal = portalRef.current?.contains(e.target);
      if (!inTrigger && !inPortal) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (collapsed) {
    return (
      <div ref={ref}>
        <button
          ref={btnRef}
          type="button"
          data-tooltip-id="sidebar-tooltip"
          data-tooltip-content={label}
          onClick={() => {
            if (!open && btnRef.current) {
              const rect = btnRef.current.getBoundingClientRect();
              setBtnPos({ top: rect.top, left: rect.right + 8 });
            }
            setOpen((o) => !o);
          }}
          className={`w-full group flex items-center justify-center gap-1 rounded-2xl px-1 py-1 text-sm transition-all mb-0.5 border
            ${
              isActiveGroup
                ? "rs-nav-active bg-gradient-to-r from-[#e45412]/14 via-[#e45412]/5 to-transparent text-[#e45412] border-[#e45412]/30 shadow-sm"
                : "rs-nav text-slate-600 hover:bg-slate-50 hover:text-[#e45412] border-transparent"
            }`}
        >
          {icon && (
            <div className="flex h-8 w-8 items-center justify-center rounded-xl text-[22px] transition-colors">
              <SidebarIcon icon={icon} className="text-[22px]" />
            </div>
          )}
        </button>

        {open &&
          btnPos &&
          createPortal(
            <div
              ref={portalRef}
              style={{
                position: "fixed",
                top: btnPos.top,
                left: btnPos.left,
                zIndex: 9999,
              }}
              className="rs-flyout w-56 rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-xl"
            >
              <div className="px-3 py-2 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-600">{label}</p>
              </div>
              <div className="py-1">
                {items.map((child) => {
                  const childActive = activeLink === child.link;
                  return (
                    <Link
                      key={child.link}
                      to={child.link}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg mx-1 transition-all
                      ${
                        childActive
                          ? "rs-nav-active bg-[#e45412]/10 text-[#e45412] font-medium"
                          : "rs-nav text-gray-700 hover:text-[#e45412] hover:bg-[#e45412]/5"
                      }`}
                    >
                      {child.icon && (
                        <SidebarIcon
                          icon={child.icon}
                          className={`text-[18px] ${childActive ? "text-[#e45412]" : "text-gray-500"}`}
                        />
                      )}
                      <span className="truncate">{child.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>,
            document.body,
          )}
      </div>
    );
  }

  return (
    <div className="mb-0.5" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`group w-full flex items-center gap-1 rounded-2xl px-2 py-1 text-sm transition-all mb-0.5 border ${
          isActiveGroup && !open
            ? "rs-nav-active bg-gradient-to-r from-[#e45412]/14 via-[#e45412]/5 to-transparent text-[#e45412] border-[#e45412]/30 shadow-sm"
            : "rs-nav text-slate-600 hover:bg-slate-50 hover:text-[#e45412] border-transparent"
        }`}
      >
        {icon && (
          <div className="flex h-8 w-8 items-center justify-center rounded-xl text-[22px] transition-colors">
            <SidebarIcon icon={icon} className="text-[22px]" />
          </div>
        )}
        <span className="flex-1 text-left font-medium truncate">{label}</span>
        <IoIosArrowDown
          className={`shrink-0 mr-1 text-current transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden pl-3 pr-1 mt-1 space-y-0.5"
          >
            {items.map((child) => {
              const childActive = activeLink === child.link;
              return (
                <Link
                  key={child.link}
                  to={child.link}
                  className={`flex items-center gap-2 px-1.5 py-1.5 rounded-xl transition-all
                    ${
                      childActive
                        ? "rs-nav-active bg-gradient-to-r from-[#e45412]/14 via-[#e45412]/5 to-transparent text-[#e45412] border border-[#e45412]/30 shadow-sm"
                        : "rs-nav text-slate-600 hover:text-[#e45412] hover:bg-slate-50 border border-transparent"
                    }`}
                >
                  {child.icon && (
                    <SidebarIcon
                      icon={child.icon}
                      className={`text-[22px] ${childActive ? "text-[#e45412]" : "text-slate-400"}`}
                    />
                  )}
                  <span style={{ fontSize: "13px" }}>{child.name}</span>
                </Link>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ===========================
 * Componentes mobile
 * =========================== */

function MobileItem({ to, icon, label, active, badgeCount }) {
  const showBadge = Number(badgeCount) > 0;
  return (
    <Link
      to={to}
      className={`rs-mnav-item flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 transition
        ${
          active
            ? "rs-mnav-active bg-white/15 text-white border border-white/20"
            : "text-white/85 hover:text-white hover:bg-white/10"
        }`}
    >
      {icon && (
        <span className="relative flex items-center justify-center">
          <SidebarIcon icon={icon} className="text-[18px] text-white/85" />
          {showBadge && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold flex items-center justify-center ring-2 ring-[#7A082D]">
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </span>
      )}
      <span className="font-medium">{label}</span>
    </Link>
  );
}

function MobileActionItem({ icon, label, onClick, hasUnread }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 transition text-white/85 hover:text-white hover:bg-white/10 text-left"
    >
      {icon && (
        <div className="relative flex items-center justify-center">
          <SidebarIcon icon={icon} className="text-[18px] text-white/85" />
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-[#7A082D]" />
          )}
        </div>
      )}
      <span className="font-medium">{label}</span>
    </button>
  );
}

function MobileGroup({
  label,
  icon,
  open,
  onToggle,
  childrenItems,
  isActiveGroup,
}) {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`rs-mnav-item w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition
          ${
            isActiveGroup
              ? "rs-mnav-active bg-white/15 text-white border border-white/20"
              : "text-white/85 hover:text-white hover:bg-white/10"
          }`}
      >
        <span className="flex items-center gap-2">
          {icon && (
            <SidebarIcon icon={icon} className="text-[18px] text-white/85" />
          )}
          <span className="font-medium">{label}</span>
        </span>
        <IoIosArrowDown
          className={`text-white/80 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden pl-2 mt-1"
          >
            {(childrenItems || []).map((child) => (
              <Link
                key={child.link}
                to={child.link}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/85 hover:text-white hover:bg-white/10 mb-1"
              >
                {child.icon && (
                  <SidebarIcon
                    icon={child.icon}
                    className="text-[16px] text-white/80"
                  />
                )}
                <span>{child.name}</span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
